import type { WmtsTile } from "@/cache/PVSTileService/TileGetters/WMTSTileGetter/WMTSTile";
import { type TileArea, tileGeometries, wrapIntoWorldZero } from "@/cache/SpatioTemporalTileCache/TileArea";
import type { ScenePublicApi } from "@/layers/Compositor";
import {
  shouldDisableTileLayerTransitions,
  shouldRequestLowQualityBackingTiles,
} from "@/layers/png/tileLayerTransitionPolicy";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import type { WMTSRepository } from "@/repository/WMTSRepository";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import type { DateTime } from "luxon";
import { calculatePriority } from "./utils";

const TILESET_PADDING = 0;
export const DEFAULT_TILE_SIZE = 256;
const BACKING_TILE_MAX_ZOOM = 3;
const BACKING_TILE_PRIORITY_BONUS = 1000;
const BACKING_TILE_COARSE_PRIORITY_STEP = 100;
type PrioritizedTile = { tile: WmtsTile; priority: number };
export type WmtsRenderableTileSets = {
  backingTiles: WmtsTile[];
  idealTiles: WmtsTile[];
  idealTilesReady: boolean;
  suppressRetainedBacking?: boolean;
  disableTransitions?: boolean;
  isActivelyAnimating?: boolean;
};

/**
 * Manages the state of WMTS tiles, deciding which tiles are needed for the current view
 * and which should be rendered.
 */
export class WMTSTileManager {
  /**
   * Deduplicates repository reprioritization for the current tile selection key.
   * Without this, render/rebuffer polling can flood reprioritize calls.
   */
  private _lastRequestedKey: string | null = null;
  private _lastSelectionTimeKey: string | null = null;
  private _lastSelectionViewKey: string | null = null;
  private _lastSelectionZoomKey: string | null = null;
  private _lastSelectionZoomValue: number | null = null;
  private _pendingTimeTransition: { timeKey: string; viewKey: string } | null = null;
  private _pendingZoomTransition: {
    timeKey: string;
    zoomKey: string;
    direction: "in" | "out";
    readyViewKey?: string;
  } | null = null;
  private readonly MAX_DATA_ZOOM = 11;

  constructor(
    private repository: WMTSRepository,
    private scene: ScenePublicApi,
    // A function to get the latest layer props, avoiding stale closures.
    private getLayerProps: () => {
      model: string;
      parameter_unit: string;
      ens_select: string;
      color_map: string;
      kind: LayerKindName;
      vertical_interpolation: string | boolean;
      calibrated: boolean;
      init_date?: string;
      api_url_suffix?: string;
      isAnimating: boolean;
      animationFps: number | null;
      isRecording: boolean;
      isScrubbing: boolean;
    },
  ) {
    // Constructor stores dependencies via parameter properties.
  }

  /**
   * Determines if the animation should pause to wait for tiles to load.
   */
  public isRebuffering(time: DateTime): boolean {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(time),
      this.getLayerProps().kind,
    );

    const { idealTiles, requestKey, prioritizedTiles, timeKey, viewKey } = this._getTileSelection(dateTimeWithOffset);

    if (idealTiles.length === 0) {
      return false;
    }

    // Keep request scheduling in sync with whichever code path asks first:
    // animation rebuffer checks and static render path now share the same trigger.
    this.requestIdealTilesIfNeeded(requestKey, prioritizedTiles);

    // A tile is considered "satisfied" if it is either loaded OR temporarily marked as terminal (with TTL)
    // by the RequestManager. This prevents the   frame from blocking when a timestamp is not yet available.
    const rm = this.repository.getRequestManager();
    const isSatisfied = idealTiles.every((t) => t.isLoaded || rm.isTerminalFailure(t.toKey()));
    this.rememberSelectionContext(timeKey, viewKey);
    return !isSatisfied;
  }

  /**
   * Returns the current-view tile pyramid split into coarse backing tiles and ideal tiles.
   * Ideal tiles should be rendered on top of backing tiles when they are available.
   */
  public getRenderableTileSets(): WmtsRenderableTileSets {
    const { idealTiles, backingTiles, requestKey, prioritizedTiles, timeKey, viewKey, idealTilesReady } =
      this._getTileSelection(this.scene.getDisplayTimeWithOffset());
    this.requestIdealTilesIfNeeded(requestKey, prioritizedTiles);
    this.rememberSelectionContext(timeKey, viewKey);

    const distinctBackingTiles = this.filterDistinctBackingTiles(backingTiles, idealTiles);

    return {
      backingTiles: distinctBackingTiles,
      idealTiles,
      idealTilesReady,
      suppressRetainedBacking: this.isPendingZoomTransition(timeKey, this.getZoomKey()),
      disableTransitions: this.shouldDisableTransitions(),
      isActivelyAnimating: this.getLayerProps().isAnimating,
    };
  }

  /**
   * Schedules repository reprioritization once per computed ideal key.
   * Called from both `isRebuffering` and `getRenderableTiles` to cover animation and static rendering paths.
   */
  private requestIdealTilesIfNeeded(requestKey: string, prioritizedTiles: PrioritizedTile[]): void {
    if (this._lastRequestedKey === requestKey) {
      return;
    }

    // Optional double protection: do not reprioritize terminal tiles at all.
    const rm = this.repository.getRequestManager();
    const filtered = prioritizedTiles.filter(({ tile }) => !tile.isLoaded && !rm.isTerminalFailure(tile.toKey()));
    this.repository.reprioritize(filtered, this.getLayerProps().isAnimating);
    this._lastRequestedKey = requestKey;
  }

  private _getTileSelection(time: DateTime): {
    idealTiles: WmtsTile[];
    idealKey: string;
    backingTiles: WmtsTile[];
    requestKey: string;
    prioritizedTiles: PrioritizedTile[];
    timeKey: string;
    viewKey: string;
    idealTilesReady: boolean;
  } {
    const timeKey = this.getTimeKey(time);
    const viewKey = this.getViewKey();
    const zoomKey = this.getZoomKey();
    const layerProps = this.getLayerProps();
    const { isAnimating, isRecording, isScrubbing } = layerProps;
    const runtime = {
      isAnimating,
      animationFps: layerProps.animationFps,
      isRecording,
      isScrubbing,
    };
    const disableTransitions = this.shouldDisableTransitions();
    const currentZoom = this.scene.getMapViewport(0).zoom;
    const { idealTiles, idealKey, prioritizedTiles: idealPrioritizedTiles } = this._getIdealTileset(time);
    const rm = this.repository.getRequestManager();
    const idealTilesReady =
      idealTiles.length > 0 && idealTiles.every((tile) => tile.isLoaded || rm.isTerminalFailure(tile.toKey()));
    const isSameViewTimeChange =
      !isAnimating &&
      this._lastSelectionTimeKey != null &&
      this._lastSelectionTimeKey !== timeKey &&
      this._lastSelectionViewKey === viewKey;
    const isZoomChange = !isAnimating && this._lastSelectionZoomKey != null && this._lastSelectionZoomKey !== zoomKey;
    const zoomDirection =
      isZoomChange && this._lastSelectionZoomValue != null
        ? currentZoom > this._lastSelectionZoomValue
          ? "in"
          : "out"
        : null;

    if (isAnimating || isRecording || isScrubbing || disableTransitions) {
      this._pendingTimeTransition = null;
      this._pendingZoomTransition = null;
    } else {
      if (
        this._pendingTimeTransition &&
        (this._pendingTimeTransition.timeKey !== timeKey || this._pendingTimeTransition.viewKey !== viewKey)
      ) {
        this._pendingTimeTransition = null;
      }
      if (
        this._pendingZoomTransition &&
        (this._pendingZoomTransition.timeKey !== timeKey ||
          this._pendingZoomTransition.zoomKey !== zoomKey ||
          (this._pendingZoomTransition.readyViewKey != null && this._pendingZoomTransition.readyViewKey !== viewKey))
      ) {
        this._pendingZoomTransition = null;
      }
      if (isSameViewTimeChange) {
        this._pendingTimeTransition = { timeKey, viewKey };
      }
      if (isZoomChange && zoomDirection != null) {
        this._pendingZoomTransition = { timeKey, zoomKey, direction: zoomDirection };
      }
    }

    const hasPendingZoomTransition = this.isPendingZoomTransition(timeKey, zoomKey);
    const pendingZoomDirection = hasPendingZoomTransition ? this._pendingZoomTransition?.direction ?? null : null;
    const suppressBackingRequests = !shouldRequestLowQualityBackingTiles(runtime, {
      pendingTimeTransition: this.isPendingTimeTransition(timeKey, viewKey),
      pendingZoomDirection,
    });
    const suppressBackingTiles = suppressBackingRequests;
    const {
      backingTiles,
      backingKey,
      prioritizedTiles: backingPrioritizedTiles,
    } = suppressBackingRequests
      ? { backingTiles: [], backingKey: "suppressed", prioritizedTiles: [] as PrioritizedTile[] }
      : this._getBackingTileset(time);

    if (!isAnimating && hasPendingZoomTransition && idealTilesReady) {
      if (pendingZoomDirection === "in" && this._pendingZoomTransition) {
        this._pendingZoomTransition = {
          ...this._pendingZoomTransition,
          readyViewKey: this._pendingZoomTransition.readyViewKey ?? viewKey,
        };
      } else {
        this._pendingZoomTransition = null;
      }
    }

    return {
      idealTiles,
      idealKey,
      backingTiles: suppressBackingTiles ? [] : backingTiles,
      requestKey: `${idealKey}|backing:${backingKey}`,
      prioritizedTiles: this.mergePrioritizedTiles(backingPrioritizedTiles, idealPrioritizedTiles),
      timeKey,
      viewKey,
      idealTilesReady,
    };
  }

  /**
   * Calculates the "ideal" set of tiles for a given time and the current viewport.
   *
   * `idealKey` must include every input that affects tile identity or data availability.
   * It is used to decide when to reschedule requests.
   */
  private _getIdealTileset(time: DateTime): {
    idealTiles: WmtsTile[];
    idealKey: string;
    prioritizedTiles: PrioritizedTile[];
  } {
    const viewport = this.scene.getMapViewport(0);
    const currentZoom = viewport.zoom;

    const tileZoom = Math.min(Math.floor(currentZoom), this.MAX_DATA_ZOOM);
    const tileViewport = this.scene.getMapViewport(Math.max(0, currentZoom - tileZoom));
    const area = this.addTileAreaOffset(tileViewport.viewportArea, TILESET_PADDING);

    return this.getTilesForArea(area, time);
  }

  private _getBackingTileset(time: DateTime): {
    backingTiles: WmtsTile[];
    backingKey: string;
    prioritizedTiles: PrioritizedTile[];
  } {
    const viewport = this.scene.getMapViewport(0);
    const currentZoom = viewport.zoom;
    const backingMaxZoom = Math.min(Math.floor(currentZoom), BACKING_TILE_MAX_ZOOM);

    const backingTileSets = [];
    for (let zoom = 0; zoom <= backingMaxZoom; zoom++) {
      const backingViewport = this.scene.getMapViewport(Math.max(0, currentZoom - zoom));
      const area = this.addTileAreaOffset(backingViewport.viewportArea, TILESET_PADDING);
      backingTileSets.push({
        zoom,
        ...this.getTilesForArea(area, time),
      });
    }

    return {
      backingTiles: backingTileSets.flatMap(({ idealTiles }) => idealTiles),
      backingKey: backingTileSets.map(({ idealKey }) => idealKey).join("|"),
      prioritizedTiles: backingTileSets.flatMap(({ zoom, prioritizedTiles }) =>
        prioritizedTiles.map(({ tile, priority }) => ({
          tile,
          priority:
            priority + BACKING_TILE_PRIORITY_BONUS + (BACKING_TILE_MAX_ZOOM - zoom) * BACKING_TILE_COARSE_PRIORITY_STEP,
        })),
      ),
    };
  }

  private rememberSelectionContext(timeKey: string, viewKey: string): void {
    this._lastSelectionTimeKey = timeKey;
    this._lastSelectionViewKey = viewKey;
    this._lastSelectionZoomKey = this.getZoomKey();
    this._lastSelectionZoomValue = this.scene.getMapViewport(0).zoom;
  }

  private shouldDisableTransitions(): boolean {
    return shouldDisableTileLayerTransitions(this.getLayerProps());
  }

  private getTimeKey(time: DateTime): string {
    return (
      roundToNearestCacheableTime(time, this.getLayerProps().kind).toUTC().toISO({ suppressMilliseconds: true }) ?? ""
    );
  }

  private getViewKey(): string {
    const viewport = this.scene.getMapViewport(0);
    const area = viewport.viewportArea;
    return `${viewport.zoom}|${area.xyMin[0]}|${area.xyMin[1]}|${area.xyMax[0]}|${area.xyMax[1]}|${area.z}`;
  }

  private getZoomKey(): string {
    const viewport = this.scene.getMapViewport(0);
    return `${viewport.zoom}|${viewport.viewportArea.z}`;
  }

  private isPendingTimeTransition(timeKey: string, viewKey: string): boolean {
    return this._pendingTimeTransition?.timeKey === timeKey && this._pendingTimeTransition?.viewKey === viewKey;
  }

  private isPendingZoomTransition(timeKey: string, zoomKey: string): boolean {
    return this._pendingZoomTransition?.timeKey === timeKey && this._pendingZoomTransition?.zoomKey === zoomKey;
  }

  private getTilesForArea(
    timeArea: TileArea,
    time: DateTime,
  ): {
    idealTiles: WmtsTile[];
    idealKey: string;
    prioritizedTiles: PrioritizedTile[];
  } {
    const area = timeArea;

    const props = this.getLayerProps();
    const dateTimeWithOffset = roundToNearestCacheableTime(time, props.kind)
      .toUTC()
      .toISO({ suppressMilliseconds: true });

    // TODO: Combine with tile.key()?
    const idealKey = `${dateTimeWithOffset}_z${area.z}_area${area.xyMin[0]}-${area.xyMax[0]}_${area.xyMin[1]}-${
      area.xyMax[1]
    }_props${props.model}-${props.parameter_unit}-${props.color_map}-${props.ens_select}-${
      props.vertical_interpolation
    }-${props.calibrated}-${props.init_date || ""}-${props.api_url_suffix || ""}`;

    const idealTiles: WmtsTile[] = [];
    const prioritizedTiles: PrioritizedTile[] = [];

    const optimalTilesWorldZero = wrapIntoWorldZero(area);

    for (const tileGeometry of tileGeometries(...optimalTilesWorldZero)) {
      const maxCoord = 2 ** tileGeometry.z - 1;
      if (tileGeometry.x < 0 || tileGeometry.x > maxCoord || tileGeometry.y < 0 || tileGeometry.y > maxCoord) {
        continue;
      }

      const tile = this.repository.getTile(
        tileGeometry,
        dateTimeWithOffset,
        props.model,
        props.parameter_unit,
        DEFAULT_TILE_SIZE,
        props.color_map,
        props.ens_select || "none",
        props.vertical_interpolation,
        props.calibrated,
        props.init_date,
        props.api_url_suffix,
      );

      const priority = calculatePriority(tileGeometry, area);
      prioritizedTiles.push({ tile, priority });
      idealTiles.push(tile);
    }

    return { idealTiles, idealKey, prioritizedTiles };
  }

  private mergePrioritizedTiles(...tileSets: PrioritizedTile[][]): PrioritizedTile[] {
    const merged = new Map<string, PrioritizedTile>();

    for (const tileSet of tileSets) {
      for (const entry of tileSet) {
        const tileKey = entry.tile.toKey();
        const existing = merged.get(tileKey);

        if (!existing || entry.priority > existing.priority) {
          merged.set(tileKey, entry);
        }
      }
    }

    return [...merged.values()];
  }

  private filterDistinctBackingTiles(backingTiles: WmtsTile[], idealTiles: WmtsTile[]): WmtsTile[] {
    const idealKeys = new Set(idealTiles.map((tile) => tile.toKey()));
    return backingTiles.filter((tile) => !idealKeys.has(tile.toKey()));
  }

  /**
   * Expands a tile area by a symmetric offset (in tile units) on x/y bounds.
   */
  private addTileAreaOffset(area: TileArea, offset: number): TileArea {
    return {
      ...area,
      xyMin: [area.xyMin[0] - offset, area.xyMin[1] - offset],
      xyMax: [area.xyMax[0] + offset, area.xyMax[1] + offset],
    };
  }

  /**
   * Preloads a buffered tile area in the opposite mouse drag direction to hide panning latency.
   */
  public preloadTilesInPanDirection(dx: number, dy: number) {
    if (!this.repository) {
      return;
    }
    const props = this.getLayerProps();
    const viewport = this.scene.getMapViewport();

    const directionX = -Math.sign(dx);
    const directionY = -Math.sign(dy);

    const preloadBuffer = 4; // Preload a buffer of X tiles in the direction of the pan

    const totalOffsetX = directionX * preloadBuffer; // offset with correct direction (- or +)
    const totalOffsetY = directionY * preloadBuffer;

    // Create a new tile area for preloading
    const preloadArea: TileArea = {
      xyMin: [viewport.viewportArea.xyMin[0] + totalOffsetX, viewport.viewportArea.xyMin[1] + totalOffsetY],
      xyMax: [viewport.viewportArea.xyMax[0] + totalOffsetX, viewport.viewportArea.xyMax[1] + totalOffsetY],
      z: viewport.viewportArea.z,
      zLodBias: 0,
    };

    const preloadGeometries = tileGeometries(...wrapIntoWorldZero(preloadArea));

    const dateTime = roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), props.kind as LayerKindName)
      .toUTC()
      .toISO({ suppressMilliseconds: true });

    for (const geometry of preloadGeometries) {
      this.repository.getTile(
        geometry,
        dateTime,
        props.model,
        props.parameter_unit,
        DEFAULT_TILE_SIZE,
        props.color_map,
        props.ens_select || "none",
        props.vertical_interpolation,
        props.calibrated,
        props.init_date,
        props.api_url_suffix,
      );
    }
  }
}
